import { productApi } from '@repo/api-client';
import type { SaleItem } from '@repo/shared-types';
import { Button } from '@repo/ui/components/button';
import { useAsync } from '@repo/ui/hooks/use-async';
import { formatCurrency } from '@repo/ui/lib/format/format-currency';
import {
  CheckCircle,
  ChevronDown,
  ChevronUp,
  CheckSquare,
  Trash2,
  X,
  ShoppingCart,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useRevalidator } from 'react-router';

interface CashierCheckoutProps {
  clubId: string;
  selectedItems: SaleItem[];
  setSelectedItemsIds: React.Dispatch<React.SetStateAction<string[]>>;
  allPlayerSales: SaleItem[];
  onActionComplete: () => void;
}

export function CashierCheckout({
  clubId,
  selectedItems,
  setSelectedItemsIds,
  allPlayerSales,
  onActionComplete,
}: CashierCheckoutProps) {
  const revalidator = useRevalidator();
  const selectedItemsIds = selectedItems.map((item) => item.id);
  const itemsContainerRef = useRef<HTMLDivElement>(null);
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);

  const total = selectedItems.reduce(
    (acc, item) => acc + (item.price ?? item.product.price) * item.quantity,
    0
  );

  const allSelected =
    allPlayerSales.length > 0 && allPlayerSales.every((item) => selectedItemsIds.includes(item.id));

  const handleSelectAll = () => {
    setSelectedItemsIds(allPlayerSales.map((item) => item.id));
  };

  const { execute: handlePay, loading: isPaying } = useAsync(async () => {
    await productApi.createSale(clubId, selectedItemsIds);
    revalidator.revalidate();
    setSelectedItemsIds([]);
    onActionComplete();
  });

  const { execute: handleDelete, loading: isDeleting } = useAsync(async () => {
    await productApi.deleteSaleItems(clubId, selectedItemsIds);
    revalidator.revalidate();
    setSelectedItemsIds([]);
    onActionComplete();
  });

  // Auto-scroll to bottom when new items added
  useEffect(() => {
    if (itemsContainerRef.current) {
      itemsContainerRef.current.scrollTo({
        top: itemsContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [selectedItemsIds.length]);

  const hasItems = selectedItemsIds.length > 0;

  // ─── Desktop sidebar ───
  const desktopContent = (
    <div className="hidden md:flex w-[320px] lg:w-[360px] flex-col border-l border-neutral-200 bg-white h-full shrink-0">
      {!hasItems ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center p-6 gap-3">
          <ShoppingCart className="size-12 text-neutral-300" />
          <p className="text-lg font-semibold text-neutral-600">Sin productos seleccionados</p>
          <p className="text-sm text-neutral-400">
            Selecciona productos de la lista para continuar
          </p>
          {allPlayerSales.length > 0 && !allSelected && (
            <Button onClick={handleSelectAll} variant="pos" size="xl" className="mt-2">
              <CheckSquare className="size-4" />
              Seleccionar todo
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-neutral-100">
            <span className="text-lg font-bold text-neutral-800">
              Resumen ({selectedItemsIds.length})
            </span>
            {allPlayerSales.length > 0 && !allSelected && (
              <button
                onClick={handleSelectAll}
                className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
              >
                Seleccionar todo
              </button>
            )}
          </div>

          {/* Items */}
          <div ref={itemsContainerRef} className="flex-1 overflow-y-auto p-4 min-h-0">
            <div className="flex flex-col gap-2">
              {selectedItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-2 rounded-lg bg-neutral-50 px-3 py-2"
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-neutral-700 truncate block">
                      {item.product.name}
                    </span>
                    {item.quantity > 1 && (
                      <span className="text-xs text-neutral-400">x{item.quantity}</span>
                    )}
                  </div>
                  <span className="text-sm font-bold text-neutral-800 shrink-0">
                    {formatCurrency((item.price ?? item.product.price) * item.quantity)}
                  </span>
                  <button
                    onClick={() =>
                      setSelectedItemsIds((prev) => prev.filter((id) => id !== item.id))
                    }
                    className="size-6 rounded-full flex items-center justify-center text-neutral-400 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Footer with total and actions */}
          <div className="border-t border-neutral-200 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-base font-medium text-neutral-600">Total</span>
              <span className="text-2xl font-bold text-neutral-900">{formatCurrency(total)}</span>
            </div>
            <Button
              onClick={handlePay}
              disabled={isPaying}
              variant="pos"
              size="xl"
              className="w-full"
            >
              <CheckCircle className="size-5" />
              Pagado
            </Button>
            <Button
              onClick={handleDelete}
              disabled={isDeleting}
              variant="destructive"
              size="xl"
              className="w-full"
            >
              <Trash2 className="size-4" />
              Eliminar seleccionados
            </Button>
          </div>
        </>
      )}
    </div>
  );

  // ─── Mobile bottom bar ───
  const mobileContent = (
    <div className="md:hidden">
      {/* Expanded overlay */}
      {isMobileExpanded && hasItems && (
        <div
          className="fixed inset-0 bg-black/30 z-40"
          onClick={() => setIsMobileExpanded(false)}
        />
      )}

      {/* Bottom bar */}
      <div
        className={`
          fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-neutral-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]
          transition-all duration-300 ease-out
          ${isMobileExpanded && hasItems ? 'max-h-[70vh]' : 'max-h-auto'}
        `}
      >
        {/* Expanded items list */}
        {isMobileExpanded && hasItems && (
          <div className="max-h-[40vh] overflow-y-auto p-4 border-b border-neutral-100">
            <div className="flex items-center justify-between mb-3">
              <span className="text-base font-bold text-neutral-800">
                Productos ({selectedItemsIds.length})
              </span>
              {allPlayerSales.length > 0 && !allSelected && (
                <button onClick={handleSelectAll} className="text-xs font-medium text-primary">
                  Seleccionar todo
                </button>
              )}
            </div>
            <div className="flex flex-col gap-2">
              {selectedItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-2 rounded-lg bg-neutral-50 px-3 py-2"
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-neutral-700 truncate block">
                      {item.product.name}
                    </span>
                  </div>
                  <span className="text-sm font-bold text-neutral-800 shrink-0">
                    {formatCurrency((item.price ?? item.product.price) * item.quantity)}
                  </span>
                  <button
                    onClick={() =>
                      setSelectedItemsIds((prev) => prev.filter((id) => id !== item.id))
                    }
                    className="size-6 rounded-full flex items-center justify-center text-neutral-400 hover:text-red-500 transition-colors shrink-0"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
              ))}
            </div>
            {/* Delete action in expanded view */}
            <div className="mt-3 pt-3 border-t border-neutral-100">
              <Button
                onClick={handleDelete}
                disabled={isDeleting}
                variant="destructive"
                size="xl"
                className="w-full"
              >
                <Trash2 className="size-4" />
                Eliminar seleccionados
              </Button>
            </div>
          </div>
        )}

        {/* Always-visible bar */}
        <div className="p-3 flex items-center gap-3 safe-area-bottom">
          {hasItems ? (
            <>
              {/* Expand toggle */}
              <button
                onClick={() => setIsMobileExpanded(!isMobileExpanded)}
                className="flex items-center gap-1.5 text-neutral-600 shrink-0"
              >
                <span className="bg-primary text-white text-xs font-bold size-6 rounded-full flex items-center justify-center">
                  {selectedItemsIds.length}
                </span>
                {isMobileExpanded ? (
                  <ChevronDown className="size-4" />
                ) : (
                  <ChevronUp className="size-4" />
                )}
              </button>

              {/* Total */}
              <div className="flex-1 min-w-0">
                <span className="text-lg font-bold text-neutral-900">{formatCurrency(total)}</span>
              </div>

              {/* Pay button */}
              <Button onClick={handlePay} disabled={isPaying} variant="pos" size="xl">
                <CheckCircle className="size-5" />
                Pagado
              </Button>
            </>
          ) : (
            <div className="flex items-center justify-between w-full">
              <span className="text-sm text-neutral-500">
                {allPlayerSales.length > 0
                  ? 'Selecciona productos para cobrar'
                  : 'No hay productos pendientes'}
              </span>
              {allPlayerSales.length > 0 && !allSelected && (
                <Button onClick={handleSelectAll} variant="pos" size="xl">
                  <CheckSquare className="size-4" />
                  Seleccionar todo
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {desktopContent}
      {mobileContent}
    </>
  );
}
